import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { MessageCircle } from 'lucide-react';
import { api, handleApiError } from '../services/api';

interface ZenviaConfig {
  ativo: boolean;
  api_token_configurado: boolean;
  api_token_mascarado: string | null;
  sender_id: string;
  base_url: string;
  template_acompanhamento_id: string;
  allowed_numbers: string;
  atualizado_em: string | null;
}

interface ZenviaForm {
  ativo: boolean;
  api_token: string;
  sender_id: string;
  base_url: string;
  template_acompanhamento_id: string;
  allowed_numbers: string;
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';

const Integracoes: React.FC = () => {
  const [config, setConfig] = useState<ZenviaConfig | null>(null);
  const [form, setForm] = useState<ZenviaForm>({
    ativo: true,
    api_token: '',
    sender_id: '',
    base_url: 'https://api.zenvia.com/v2',
    template_acompanhamento_id: '',
    allowed_numbers: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const aplicarConfig = (c: ZenviaConfig) => {
    setConfig(c);
    setForm({
      ativo: c.ativo,
      api_token: '',
      sender_id: c.sender_id,
      base_url: c.base_url,
      template_acompanhamento_id: c.template_acompanhamento_id,
      allowed_numbers: c.allowed_numbers
    });
  };

  useEffect(() => {
    const carregar = async () => {
      try {
        const response = await api.get('/integracoes/zenvia');
        aplicarConfig(response.data.config);
      } catch (error) {
        toast.error(handleApiError(error));
      } finally {
        setLoading(false);
      }
    };
    void carregar();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await api.put('/integracoes/zenvia', form);
      aplicarConfig(response.data.config);
      toast.success('Configuração do WhatsApp salva');
    } catch (error: any) {
      const detalhes = error.response?.data?.details;
      toast.error(detalhes?.length ? detalhes.map((d: { message: string }) => d.message).join('\n') : handleApiError(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-gray-500">Carregando configurações...</p>;
  }

  const listaRestrita = form.allowed_numbers.trim().length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Integrações</h1>
        <p className="text-gray-600 mt-1">Configuração dos serviços externos usados pelo Nexus.</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <MessageCircle className="h-5 w-5 text-green-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">WhatsApp (Zenvia)</h2>
              <p className="text-sm text-gray-600">
                Envia à família o link do portal Family quando uma OS do Sisfuner entra em monitoramento.
              </p>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 whitespace-nowrap">
            <input
              type="checkbox"
              checked={form.ativo}
              onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
            />
            Envio ativo
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Token da API (X-API-Token)</label>
            <input
              type="password"
              value={form.api_token}
              onChange={(e) => setForm({ ...form, api_token: e.target.value })}
              className={inputClass}
              placeholder={
                config?.api_token_configurado
                  ? `Configurado (${config.api_token_mascarado}) — deixe em branco para manter`
                  : 'Cole o token da Zenvia'
              }
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Remetente (Sender ID)</label>
            <input
              type="text"
              value={form.sender_id}
              onChange={(e) => setForm({ ...form, sender_id: e.target.value.replace(/\D/g, '') })}
              className={inputClass}
              placeholder="5511917001259"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ID do template de acompanhamento</label>
            <input
              type="text"
              value={form.template_acompanhamento_id}
              onChange={(e) => setForm({ ...form, template_acompanhamento_id: e.target.value })}
              className={inputClass}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">URL base da API</label>
            <input
              type="url"
              value={form.base_url}
              onChange={(e) => setForm({ ...form, base_url: e.target.value })}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Números permitidos (testes)</label>
            <input
              type="text"
              value={form.allowed_numbers}
              onChange={(e) => setForm({ ...form, allowed_numbers: e.target.value })}
              className={inputClass}
              placeholder="Vazio = envia para qualquer número"
            />
            <p className={`text-xs mt-1 ${listaRestrita ? 'text-amber-700' : 'text-gray-500'}`}>
              {listaRestrita
                ? 'Modo teste: só estes números recebem mensagens. Separe por vírgula.'
                : 'Produção: todas as famílias recebem mensagens.'}
            </p>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-gray-600">
          Variáveis do template: <span className="font-mono">{'{{1}}'}</span> nome do contratante e{' '}
          <span className="font-mono">{'{{2}}'}</span> nome do falecido.
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {config?.atualizado_em ? `Última atualização: ${new Date(config.atualizado_em).toLocaleString('pt-BR')}` : ''}
          </span>
          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Integracoes;
